import { ExhibitionComment } from './wall-api.service';

export const WALL_VISIBLE_COMMENTS_LIMIT = 3;

export interface VisibleCommentThread {
  /** Original root comment reference (safe for edit/reply bindings). */
  comment: ExhibitionComment;
  /** Original reply references included in the visible window. */
  replies: ExhibitionComment[];
}

export function commentCreatedMs(c?: { created?: string | Date } | null): number {
  const t = new Date(c?.created as string).getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function sortCommentsNewestFirst<T extends { created?: string | Date }>(
  list: T[] | null | undefined
): T[] {
  return (list || [])
    .slice()
    .sort((a, b) => commentCreatedMs(b) - commentCreatedMs(a));
}

/** Total root comments + replies. */
export function countCommentNodes(
  roots: ExhibitionComment[] | null | undefined
): number {
  let n = 0;
  (roots || []).forEach((r) => {
    n += 1 + (r.replies?.length || 0);
  });
  return n;
}

/**
 * Newest-first visual order (root then its replies), keep only the first `limit`
 * nodes — as if cutting the top of the currently displayed list.
 */
export function sliceVisibleCommentThreads(
  roots: ExhibitionComment[] | null | undefined,
  limit = WALL_VISIBLE_COMMENTS_LIMIT
): VisibleCommentThread[] {
  type Flat =
    | { kind: 'root'; root: ExhibitionComment }
    | { kind: 'reply'; root: ExhibitionComment; reply: ExhibitionComment };

  const flat: Flat[] = [];
  for (const root of sortCommentsNewestFirst(roots)) {
    flat.push({ kind: 'root', root });
    for (const reply of sortCommentsNewestFirst(root.replies)) {
      flat.push({ kind: 'reply', root, reply });
    }
  }

  const top = flat.slice(0, Math.max(0, limit));
  const result: VisibleCommentThread[] = [];
  const byId = new Map<string, VisibleCommentThread>();

  for (const item of top) {
    if (item.kind === 'root') {
      if (byId.has(item.root._id)) {
        continue;
      }
      const thread: VisibleCommentThread = {
        comment: item.root,
        replies: [],
      };
      byId.set(item.root._id, thread);
      result.push(thread);
      continue;
    }

    let thread = byId.get(item.root._id);
    if (!thread) {
      thread = { comment: item.root, replies: [] };
      byId.set(item.root._id, thread);
      result.push(thread);
    }
    thread.replies.push(item.reply);
  }

  return result;
}

/** Full thread list (newest first) without truncation. */
export function allCommentThreads(
  roots: ExhibitionComment[] | null | undefined
): VisibleCommentThread[] {
  return sortCommentsNewestFirst(roots).map((comment) => ({
    comment,
    replies: sortCommentsNewestFirst(comment.replies),
  }));
}

/** Spacer height while comments are in-flight, so enlarge does not jump when they arrive. */
export function wallCommentsLoadReservePx(
  commentCount: number | null | undefined,
  loadedCount = 0
): number {
  if (loadedCount > 0) {
    return 0;
  }
  const n = Math.max(0, Number(commentCount) || 0);
  if (n <= 0) {
    return 0;
  }
  return Math.min(n, 12) * 68;
}
